import React, { useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/axios';
import SearchableSelect from './SearchableSelect';

/**
 * Slack channel members who matched no employee.
 *
 * Ingest resolves identity by exact email only and never guesses, so this is
 * where a human resolves the remainder. Candidates are ranked suggestions, not
 * decisions. Ignoring a member is how a deliberately-untracked person leaves
 * this list, which keeps the banner meaningful: it appears only when there is
 * something new to act on.
 */
// Above this many members the list is collapsed by default. Each member card
// carries a searchable employee picker, so an unfiltered list of nine pushed the
// matrix - the actual point of the page - entirely below the fold.
const AUTO_COLLAPSE_ABOVE = 2;

const UnlinkedMembersBanner = ({ unlinked, canManage, onChanged }) => {
  const [errors, setErrors] = useState({});
  const [ignoring, setIgnoring] = useState(null);
  const [ignoreReason, setIgnoreReason] = useState('');
  const [employees, setEmployees] = useState([]);
  const [expanded, setExpanded] = useState(unlinked.length <= AUTO_COLLAPSE_ABOVE);

  const needsPicker = unlinked.some((u) => !u.candidates || u.candidates.length === 0);

  useEffect(() => {
    if (!canManage || !needsPicker || !expanded) return;
    api.get('/api/employees?limit=500')
      .then((res) => setEmployees((res.data.data && res.data.data.employees) || res.data.data || []))
      .catch(() => setEmployees([]));
  }, [canManage, needsPicker, expanded]);

  const link = async (slackUserId, employeeId) => {
    setErrors((e) => ({ ...e, [slackUserId]: null }));
    try {
      await api.put(`/api/employees/${employeeId}/slack-link`, { slack_user_id: slackUserId });
      toast.success('Slack account linked');
      onChanged();
    } catch (err) {
      const message = (err.response && err.response.data && err.response.data.message)
        || 'Could not link the account.';
      setErrors((e) => ({ ...e, [slackUserId]: message }));
    }
  };

  const confirmIgnore = async (member) => {
    try {
      await api.post('/api/attendance/ignored', {
        slack_user_id: member.slack_user_id,
        slack_email: member.email,
        reason: ignoreReason.trim() || null,
      });
      toast.success('Member marked untracked');
      setIgnoring(null);
      setIgnoreReason('');
      onChanged();
    } catch (err) {
      setErrors((e) => ({ ...e, [member.slack_user_id]: 'Could not ignore the member.' }));
    }
  };

  return (
    <div data-testid="unlinked-banner" className="rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-amber-900">
            {unlinked.length} Slack member{unlinked.length === 1 ? '' : 's'} not linked to an employee
          </h3>
          <p className="mt-1 text-xs text-amber-800">
            They post in #availability but do not appear in the matrix. Link them to an employee,
            or mark them untracked if they are not staff.
          </p>

          <button
            type="button"
            data-testid="unlinked-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-900 underline"
          >
            {expanded
              ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
            {expanded ? 'Hide' : `Review ${unlinked.length === 1 ? 'it' : 'them'}`}
          </button>

          {/* The `hidden` attribute, not a utility class: it also hides the list
              from assistive tech, and it does not depend on Tailwind loading. */}
          <ul hidden={!expanded} className="mt-3 space-y-3">
            {unlinked.map((m) => (
              <li key={m.slack_user_id} className="rounded-md border border-amber-200 bg-white p-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-sm font-medium text-gray-900">{m.name || m.slack_user_id}</span>
                  <span className="text-xs text-gray-500">{m.email || 'no email visible'}</span>
                </div>

                {canManage && (
                  <div className="mt-2 space-y-2">
                    {m.candidates && m.candidates.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {m.candidates.map((c) => (
                          <button
                            key={c.employee_id}
                            type="button"
                            onClick={() => link(m.slack_user_id, c.employee_id)}
                            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                          >
                            {c.name} ({c.email}) - distance {c.distance}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs text-gray-500">No close match. Pick an employee:</p>
                        <SearchableSelect
                          options={employees.map((e) => ({
                            value: e.id,
                            label: `${e.first_name} ${e.last_name} (${e.email})`,
                          }))}
                          value=""
                          onChange={(value) => value && link(m.slack_user_id, value)}
                          placeholder="Search employees"
                        />
                      </div>
                    )}

                    {ignoring === m.slack_user_id ? (
                      <div className="space-y-2">
                        <label htmlFor={`ignore-${m.slack_user_id}`} className="block text-xs font-medium text-gray-700">
                          Why is this member untracked?
                        </label>
                        <input
                          id={`ignore-${m.slack_user_id}`}
                          type="text"
                          value={ignoreReason}
                          onChange={(e) => setIgnoreReason(e.target.value)}
                          className="block w-full rounded border border-gray-300 p-1.5 text-xs"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => confirmIgnore(m)}
                            className="rounded bg-gray-800 px-2 py-1 text-xs font-medium text-white hover:bg-gray-900"
                          >
                            Confirm ignore
                          </button>
                          <button
                            type="button"
                            onClick={() => { setIgnoring(null); setIgnoreReason(''); }}
                            className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIgnoring(m.slack_user_id)}
                        className="text-xs text-gray-600 underline hover:text-gray-800"
                      >
                        Ignore - not an employee
                      </button>
                    )}
                  </div>
                )}

                {errors[m.slack_user_id] && (
                  <p className="mt-2 text-xs text-red-700">{errors[m.slack_user_id]}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UnlinkedMembersBanner;
